import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/react-hooks';
import {
  CREATE_JOB_TRANSLATION,
  UPDATE_JOB_TRANSLATION,
  DELETE_JOB_TRANSLATION,
} from '../apollo/mutation';
import {
  Card,
  Button,
  FormLayout,
  Select,
  TextField,
  Modal,
  ResourceList,
  ResourceItem,
  Stack,
  Toast,
  EmptyState,
  TextContainer,
  Subheading,
} from '@shopify/polaris';
import { Editor } from '@tinymce/tinymce-react';
import { CirclePlusMinor } from '@shopify/polaris-icons';

const SUPPORTED_LANGUAGES = [
  //{ label: 'English', value: 'en' }, DEFAULT LANGUAGE
  { label: 'Spanish', value: 'es' },
  { label: 'French', value: 'fr' },
  { label: 'German', value: 'de' },
  { label: 'Italian', value: 'it' },
  { label: 'Portuguese', value: 'pt' },
  { label: 'Japanese', value: 'ja' },
  { label: 'Korean', value: 'ko' },
  { label: 'Chinese', value: 'zh' },
  { label: 'Arabic', value: 'ar' },
  { label: 'Russian', value: 'ru' }
];

const JobTranslationManager = ({ job, shopifyToken, onTranslationUpdate }) => {
  // Early return if job is not loaded
  if (!job || !job.id) {
    return (
      <Card title="Job Translations" sectioned>
        <div>Loading job data...</div>
      </Card>
    );
  }
  const [selectedLocale, setSelectedLocale] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTranslation, setEditingTranslation] = useState(null);
  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);
  const [translationToDelete, setTranslationToDelete] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    isActive: true,
  });

  const [createTranslation] = useMutation(CREATE_JOB_TRANSLATION, {
    refetchQueries: ['job']
  });
  const [updateTranslation] = useMutation(UPDATE_JOB_TRANSLATION, {
    refetchQueries: ['job']
  });
  const [deleteTranslation] = useMutation(DELETE_JOB_TRANSLATION, {
    refetchQueries: ['job']
  });



  // Get available languages (languages that don't have translations yet)
  // Handle case where jobTranslations might be undefined or null
  const jobTranslations = job.jobTranslations || [];
  const availableLanguages = SUPPORTED_LANGUAGES.filter(
    lang => !jobTranslations.some(t => t.locale === lang.value)
  );



  const handleAddTranslation = () => {
    if (!selectedLocale) return;
    
    setFormData({
      title: job.title || '',
      description: job.description || '',
      isActive: true,
    });
    setEditingTranslation(null);
    setIsModalOpen(true);
  };

  const handleEditTranslation = (translation) => {
    setSelectedLocale(translation.locale);
    setFormData({
      title: translation.title || '',
      description: translation.description || '',
      isActive: translation.isActive,
    });
    setEditingTranslation(translation);
    setIsModalOpen(true);
  };

  const handleDeleteTranslation = (translationId) => {
    setTranslationToDelete(translationId);
    setIsConfirmDeleteModalOpen(true);
  };

  const confirmDeleteTranslation = async () => {
    if (!translationToDelete) return;
    
    try {
      await deleteTranslation({
        variables: {
          shopifyToken,
          translationId: translationToDelete,
        },
      });
      showToast('Translation deleted successfully');
      if (onTranslationUpdate) onTranslationUpdate();
    } catch (error) {
      showToast('Error deleting translation: ' + error.message);
    } finally {
      setIsConfirmDeleteModalOpen(false);
      setTranslationToDelete(null);
    }
  };

  const cancelDeleteTranslation = () => {
    setIsConfirmDeleteModalOpen(false);
    setTranslationToDelete(null);
  };

  const handleSaveTranslation = async () => {
    setIsSaving(true);
    try {
      // Clean up form data - remove empty strings for optional fields
      const cleanFormData = Object.fromEntries(
        Object.entries(formData).map(([key, value]) => [
          key, 
          value === '' ? null : value
        ])
      );
      
      const variables = {
        shopifyToken,
        translationInput: {
          locale: selectedLocale,
          ...cleanFormData,
        },
      };

      if (editingTranslation) {
        variables.translationId = editingTranslation.id;
        const result = await updateTranslation({ variables });
        if (result.data?.updateJobTranslation?.errors?.length > 0) {
          throw new Error(result.data.updateJobTranslation.errors.join(', '));
        }
        showToast('Translation updated successfully');
        
        // Force refresh the job data to get updated translations
        if (onTranslationUpdate) {
          setTimeout(() => {
            onTranslationUpdate();
          }, 100);
        }
      } else {
        variables.jobId = job.id;
        const result = await createTranslation({ variables });
        if (result.data?.createJobTranslation?.errors?.length > 0) {
          throw new Error(result.data.createJobTranslation.errors.join(', '));
        }
        showToast('Translation created successfully');
        
        // Force refresh the job data to get updated translations
        if (onTranslationUpdate) {
          setTimeout(() => {
            onTranslationUpdate();
          }, 100);
        }
      }

      setIsModalOpen(false);
    } catch (error) {
      console.error('Error saving translation:', error);
      console.error('Error details:', {
        message: error.message,
        graphQLErrors: error.graphQLErrors,
        networkError: error.networkError,
        extraInfo: error.extraInfo
      });
      
      // Handle session expiration
      if (error.message.includes('session') || error.message.includes('authentication')) {
        showToast('Session expired. Please refresh the page and try again.');
      } else {
        showToast('Error saving translation: ' + error.message);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const showToast = (message) => {
    setToastMessage(message);
    setToastActive(true);
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const renderTranslationItem = (translation) => (
    <ResourceItem
      id={translation.id}
      accessibilityLabel={`Edit translation for ${translation.localeName}`}
      shortcutActions={[
        {
          content: (
            <>
              Edit
            </>
          ),
          onAction: () => handleEditTranslation(translation),
        },
        {
          content: (
            <>
              Delete
            </>
          ),
          destructive: true,
          onAction: () => handleDeleteTranslation(translation.id),
        },
      ]}
    >
      <TextContainer>
        <Subheading>{translation.localeName}</Subheading>
        <p style={{ margin: '4px 0', color: '#637381' }}>
          {translation.title || 'No title'}
        </p>
        <p style={{ margin: '4px 0', fontSize: '12px', color: '#8c9196' }}>
          Updated: {translation.updatedAt}
        </p>
      </TextContainer>
    </ResourceItem>
  );
  
  

  return (
    <>
      <Card sectioned>
        <Stack vertical spacing="loose">

          <Stack distribution="equalSpacing" alignment="center">
            <Subheading>Available Languages</Subheading>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
              <div style={{ minWidth: '200px' }}>
                <Select
                  label=""
                  labelInline
                  options={availableLanguages}
                  value={selectedLocale}
                  onChange={setSelectedLocale}
                  placeholder="Select a language"
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Button
                  icon={CirclePlusMinor}
                  onClick={handleAddTranslation}
                  disabled={!selectedLocale}
                >
                  Add Translation
                </Button>
              </div>
            </div>
          </Stack>

          {jobTranslations && jobTranslations.length > 0 ? (
            <div className={'JobTranslations__list'}>
              <ResourceList
                items={jobTranslations}
                renderItem={renderTranslationItem}
              /></div>
            ) : (
            <EmptyState
              heading="No translations available"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>Add translations to make your job posting available in multiple languages.</p>
            </EmptyState>
          )}
        </Stack>
      </Card>

      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingTranslation ? 'Edit Translation' : 'Add Translation'}
        primaryAction={{
          content: 'Save',
          onAction: handleSaveTranslation,
          loading: isSaving,
          disabled: isSaving,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setIsModalOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            {!editingTranslation && (
              <Select
                label="Language"
                options={availableLanguages}
                value={selectedLocale}
                onChange={setSelectedLocale}
                helpText="Select the language for this translation"
              />
            )}

            <TextField
              label="Title"
              value={formData.title}
              onChange={(value) => handleFormChange('title', value)}
              helpText="Job title in the selected language"
            />

            <div>
              <label>Description</label>
              <Editor
                value={formData.description}
                onEditorChange={(content) => handleFormChange('description', content)}
                init={{
                  height: 300,
                  menubar: false,
                  plugins: [
                    'advlist autolink lists link image charmap print preview anchor',
                    'searchreplace visualblocks code fullscreen',
                    'insertdatetime media table paste code help wordcount'
                  ],
                  toolbar:
                    'undo redo | formatselect | bold italic backcolor | \
                    alignleft aligncenter alignright alignjustify | \
                    bullist numlist outdent indent | removeformat | help'
                }}
              />
            </div>
          </FormLayout>
        </Modal.Section>
      </Modal>

      {toastActive && (
        <Toast
          content={toastMessage}
          onDismiss={() => setToastActive(false)}
          duration={4000}
        />
      )}

      <Modal
        open={isConfirmDeleteModalOpen}
        onClose={cancelDeleteTranslation}
        title="Delete Translation"
        primaryAction={{
          content: 'Delete',
          onAction: confirmDeleteTranslation,
          destructive: true,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: cancelDeleteTranslation,
          },
        ]}
      >
        <Modal.Section>
          <p>Are you sure you want to delete this translation? This action cannot be undone.</p>
        </Modal.Section>
      </Modal>
    </>
  );
};

export default JobTranslationManager;
